import { jsPDF } from "jspdf";
import JsBarcode from "jsbarcode";

// Physical label: 62mm x 100mm portrait (Brother QL die-cut / continuous).
const W = 62;
const H = 100;
const M = 3.5; // side margin
const CW = W - 2 * M;

function barcodeDataUrl(text) {
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, text, { format: "CODE128", displayValue: false, margin: 0, width: 2, height: 60 });
  return canvas.toDataURL("image/png");
}

function drawLabel(doc, label, codeType, qrImg) {
  const { roll, product, manufacturer, series } = label;
  let y = 6;

  // brand · series
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(7);
  const brand = [manufacturer?.name, series?.name].filter(Boolean).join("  ·  ").toUpperCase();
  doc.text(brand || "—", M, y);
  y += 4.5;

  // product name (bold, wraps)
  doc.setFont("helvetica", "bold");
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(15);
  const nameLines = doc.splitTextToSize(product.name || "", CW);
  doc.text(nameLines, M, y);
  y += nameLines.length * 5.6 + 0.5;

  // material length — directly below the name at the SAME font size
  const len = roll.status === "incoming" ? roll.initial_length_m : roll.current_length_m;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(37, 99, 235);
  doc.text(`${Number(len).toFixed(2)} m`, M, y + 4.6);
  y += 8.5;

  if (roll.status === "incoming") {
    doc.setFillColor(254, 243, 199);
    doc.setDrawColor(252, 211, 77);
    doc.roundedRect(M, y, 20, 4.5, 0.6, 0.6, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(5.5);
    doc.setTextColor(146, 64, 14);
    doc.text("INCOMING", M + 2, y + 3);
    y += 6.5;
  }

  // SKU chip (moved down, below the length)
  doc.setFillColor(241, 245, 249);
  doc.roundedRect(M, y, CW, 6, 0.8, 0.8, "F");
  doc.setFont("courier", "normal");
  doc.setFontSize(8);
  doc.setTextColor(15, 23, 42);
  doc.text(String(roll.sku || ""), M + 2, y + 4);
  y += 9;

  // field grid (2 columns) — Length is shown above, so: Width|Batch, Mfg|Expires, Location
  const col2 = M + CW / 2;
  const exp = new Date(roll.expiry_date);
  const daysToExp = Math.ceil((exp - new Date()) / 86400000);
  const expired = daysToExp < 0;
  const expiring = daysToExp >= 0 && daysToExp <= 90;

  const field = (lbl, value, x, yy, color) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(5.5);
    doc.setTextColor(100, 116, 139);
    doc.text(String(lbl).toUpperCase(), x, yy);
    doc.setFont("courier", "bold");
    doc.setFontSize(8.5);
    if (color) doc.setTextColor(color[0], color[1], color[2]);
    else doc.setTextColor(15, 23, 42);
    doc.text(String(value), x, yy + 4);
  };

  field("Width", `${Number(roll.width_m).toFixed(2)}m`, M, y);
  field("Batch", roll.batch_number || "—", col2, y);
  y += 8;
  field("Expires", roll.expiry_date || "—", M, y, expired ? [185, 28, 28] : expiring ? [180, 83, 9] : null);
  field("Location", roll.location || "—", col2, y);
  y += 9;

  // code
  if (codeType === "barcode") {
    const img = barcodeDataUrl(roll.sku);
    const bh = 16;
    doc.addImage(img, "PNG", M, y, CW, bh);
  } else if (qrImg) {
    const size = Math.min(46, H - y - 4);
    const cx = (W - size) / 2;
    doc.addImage(qrImg, "PNG", cx, y, size, size);
  }
}

// Print a jsPDF doc as seamlessly as the platform allows.
// Desktop: hidden iframe auto-triggers the print dialog.
// Mobile: opens the PDF (with an embedded auto-print action) so the OS print
// sheet appears — fully silent printing isn't possible from a mobile browser.
export function printPdfDoc(doc) {
  doc.autoPrint();
  const blobUrl = doc.output("bloburl");
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");

  if (isMobile) {
    const win = window.open(blobUrl, "_blank");
    if (!win) {
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = "vwa-labels.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    return;
  }

  let frame = document.getElementById("vs-pdf-print-frame");
  if (frame) frame.remove();
  frame = document.createElement("iframe");
  frame.id = "vs-pdf-print-frame";
  Object.assign(frame.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
  frame.onload = () => {
    setTimeout(() => {
      try { frame.contentWindow.focus(); frame.contentWindow.print(); }
      catch (e) { window.open(blobUrl, "_blank"); }
    }, 400);
  };
  frame.src = blobUrl;
  document.body.appendChild(frame);
}

// labels: [{ roll, product, manufacturer, series, scanTarget }]
// qrImages: { [rollId]: pngDataUrl }
export function buildLabelsPdf(labels, codeType, qrImages = {}) {
  const doc = new jsPDF({ unit: "mm", format: [W, H], orientation: "portrait" });
  labels.forEach((label, i) => {
    if (i > 0) doc.addPage([W, H], "portrait");
    drawLabel(doc, label, codeType, qrImages[label.roll.id]);
  });
  return doc;
}
